import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  Category,
  CategoryId,
  QuickAdd,
  User,
  UserPreferences,
} from "../api";
import { ApiError } from "../api";
import { OTHER_CATEGORY_ID, fmt, slugifyCategoryId } from "../constants";
import { type Lang, setLanguage } from "../i18n";
import { Icon } from "../icons";

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;
const QUICK_ADDS_MAX = 20;
const CATEGORIES_MAX = 30;
const CATEGORY_LABEL_MAX = 30;
const CATEGORY_EMOJI_MAX = 8;

const EMOJI_PALETTE = [
  "🍔", "🍕", "🍣", "🍜", "🍎", "🥗", "☕", "🍺", "🍷", "🥐", "🍰", "🍫",
  "🚗", "🚌", "🚆", "✈️", "⛽", "🚲", "🛵", "🚕",
  "💡", "💰", "🏠", "📱", "💳", "🧾", "📦", "✉️",
  "🛍️", "👕", "👟", "💄", "💍", "🎒",
  "🎬", "🎮", "🎵", "📚", "🎟️", "🎉", "🏟️",
  "💊", "🏥", "🏋️", "🧘", "💉", "🚑",
  "🎁", "🐶", "🐱", "🌱", "🛠️", "⚙️", "🌍", "⭐", "❤️", "✨",
];

const SUPPORT_EMAIL = "gjelirefail@gmail.com";

interface Props {
  user: User;
  isDesktop: boolean;
  onUpdate: (
    patch: Partial<Pick<User, "username" | "budget" | "quickAdds" | "categories">>,
  ) => Promise<void>;
  onSetPreference: (
    key: keyof UserPreferences,
    value: boolean,
  ) => Promise<void>;
  onBack: () => void;
  onDeleteAccount: () => void | Promise<void>;
  onLogout: () => void | Promise<void>;
  onOpenPrivacy: () => void;
  onOpenTerms: () => void;
}

export function SettingsScreen({
  user,
  isDesktop,
  onUpdate,
  onSetPreference,
  onBack,
  onDeleteAccount,
  onLogout,
  onOpenPrivacy,
  onOpenTerms,
}: Props) {
  const { t, i18n } = useTranslation();
  const [budget, setBudget] = useState(String(user.budget));
  const [username, setUsername] = useState(user.username);
  const [editingName, setEditingName] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<Category[]>(user.categories ?? []);
  const [editingCatId, setEditingCatId] = useState<CategoryId | null>(null);
  const [catLabelDraft, setCatLabelDraft] = useState("");
  const [catEmojiDraft, setCatEmojiDraft] = useState("");
  const [newCatLabel, setNewCatLabel] = useState("");
  const [newCatEmoji, setNewCatEmoji] = useState("");
  const [pickerOpen, setPickerOpen] = useState<"new" | "edit" | null>(null);
  const togglePicker = (which: "new" | "edit") =>
    setPickerOpen((p) => (p === which ? null : which));
  const [quickAdds, setQuickAdds] = useState<QuickAdd[]>(user.quickAdds ?? []);
  const [qaLabel, setQaLabel] = useState("");
  const [qaAmount, setQaAmount] = useState("");
  const [qaCat, setQaCat] = useState<CategoryId>(
    user.categories?.[0]?.id ?? OTHER_CATEGORY_ID,
  );

  const qaAmountNum = parseFloat(qaAmount) || 0;
  const qaValid = qaLabel.trim().length > 0 && qaAmountNum > 0 && quickAdds.length < QUICK_ADDS_MAX;

  const newCatId = useMemo(() => slugifyCategoryId(newCatLabel), [newCatLabel]);
  const newCatValid =
    newCatLabel.trim().length > 0 &&
    newCatLabel.trim().length <= CATEGORY_LABEL_MAX &&
    newCatEmoji.trim().length > 0 &&
    newCatEmoji.length <= CATEGORY_EMOJI_MAX &&
    newCatId.length > 0 &&
    !categories.some((c) => c.id === newCatId) &&
    categories.length < CATEGORIES_MAX;

  const addCategory = () => {
    if (!newCatValid) return;
    const nextOrder =
      categories.reduce((m, c) => Math.max(m, c.order), -1) + 1;
    setCategories((prev) => [
      ...prev,
      {
        id: newCatId,
        label: newCatLabel.trim(),
        emoji: newCatEmoji,
        order: nextOrder,
      },
    ]);
    setNewCatLabel("");
    setNewCatEmoji("");
  };

  const beginEditCategory = (cat: Category) => {
    setEditingCatId(cat.id);
    setCatLabelDraft(cat.label);
    setCatEmojiDraft(cat.emoji);
    setPickerOpen(null);
  };

  const cancelEditCategory = () => {
    setEditingCatId(null);
    setCatLabelDraft("");
    setCatEmojiDraft("");
    setPickerOpen(null);
  };

  const saveEditCategory = () => {
    const trimmed = catLabelDraft.trim();
    if (
      !editingCatId ||
      trimmed.length === 0 ||
      trimmed.length > CATEGORY_LABEL_MAX ||
      catEmojiDraft.trim().length === 0 ||
      catEmojiDraft.length > CATEGORY_EMOJI_MAX
    ) {
      return;
    }
    setCategories((prev) =>
      prev.map((c) =>
        c.id === editingCatId
          ? { ...c, label: trimmed, emoji: catEmojiDraft }
          : c,
      ),
    );
    cancelEditCategory();
  };

  const removeCategory = (id: CategoryId) => {
    if (id === OTHER_CATEGORY_ID) return;
    setCategories((prev) => prev.filter((c) => c.id !== id));
    if (editingCatId === id) cancelEditCategory();
    if (qaCat === id) setQaCat(OTHER_CATEGORY_ID);
    setQuickAdds((prev) =>
      prev.map((qa) => (qa.cat === id ? { ...qa, cat: OTHER_CATEGORY_ID } : qa)),
    );
  };

  const addQuickAdd = () => {
    if (!qaValid) return;
    setQuickAdds((prev) => [...prev, { label: qaLabel.trim(), amount: qaAmountNum, cat: qaCat }]);
    setQaLabel("");
    setQaAmount("");
  };

  const removeQuickAdd = (idx: number) => {
    setQuickAdds((prev) => prev.filter((_, i) => i !== idx));
  };

  const quickAddsChanged = useMemo(() => {
    const a = user.quickAdds ?? [];
    if (a.length !== quickAdds.length) return true;
    return a.some((x, i) => x.label !== quickAdds[i]?.label || x.amount !== quickAdds[i]?.amount || x.cat !== quickAdds[i]?.cat);
  }, [user.quickAdds, quickAdds]);

  const categoriesChanged = useMemo(() => {
    const a = user.categories ?? [];
    if (a.length !== categories.length) return true;
    return a.some(
      (x, i) =>
        x.id !== categories[i]?.id ||
        x.label !== categories[i]?.label ||
        x.emoji !== categories[i]?.emoji ||
        x.order !== categories[i]?.order,
    );
  }, [user.categories, categories]);

  const usernameError = useMemo(() => {
    if (username === user.username) return null;
    if (username.length < 3) return t("errors.usernameMin");
    if (!USERNAME_RE.test(username)) return t("errors.usernameFormat");
    return null;
  }, [username, user.username, t]);

  const budgetNum = parseFloat(budget) || 0;

  const saveAll = async () => {
    setError(null);
    const patch: Partial<
      Pick<User, "username" | "budget" | "quickAdds" | "categories">
    > = {};
    if (budgetNum > 0 && budgetNum !== user.budget) patch.budget = budgetNum;
    if (!usernameError && username !== user.username) patch.username = username;
    if (categoriesChanged) patch.categories = categories;
    if (quickAddsChanged) patch.quickAdds = quickAdds;
    if (Object.keys(patch).length === 0) {
      onBack();
      return;
    }
    setSaving(true);
    try {
      await onUpdate(patch);
      onBack();
    } catch (e) {
      if (e instanceof ApiError && e.code === "USERNAME_TAKEN") setError(t("errors.usernameTaken"));
      else setError(e instanceof Error ? e.message : t("errors.saveFailed"));
      setSaving(false);
    }
  };

  const wrap: React.CSSProperties = isDesktop
    ? { width: "100%", maxWidth: 560, margin: "0 auto", padding: "0 0 40px" }
    : { flex: 1, overflow: "auto", padding: "24px 24px 30px" };

  const currentLang = (i18n.language === "en" ? "en" : "al") as Lang;

  return (
    <div style={wrap}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <button
          onClick={onBack}
          aria-label={t("settings.back")}
          style={{
            width: 36,
            height: 36,
            borderRadius: 999,
            background: "var(--surface)",
            border: "1px solid var(--line)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {Icon.back()}
        </button>
        <div style={{ fontSize: 15, fontWeight: 600 }}>{t("settings.title")}</div>
        <button
          onClick={saveAll}
          disabled={saving}
          style={{ fontSize: 14, fontWeight: 600, color: "var(--accent)" }}
        >
          {saving ? t("common.saving") : t("common.save")}
        </button>
      </div>

      <Group title={t("settings.account")}>
        <Row label={t("settings.email")}>
          <span style={{ fontSize: 14, color: "var(--ink-2)" }}>{user.email}</span>
        </Row>
        <Row label={t("settings.username")}>
          {!editingName ? (
            <button
              onClick={() => setEditingName(true)}
              className="num"
              style={{ fontSize: 14, fontWeight: 500, color: "var(--ink)" }}
            >
              @{user.username}
            </button>
          ) : (
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s/g, ""))}
              autoFocus
              aria-label={t("settings.editUsername")}
              style={{
                border: "1px solid " + (usernameError ? "var(--danger)" : "var(--line)"),
                borderRadius: 8,
                padding: "6px 10px",
                fontSize: 16,
                outline: "none",
                width: 160,
                textAlign: "right",
              }}
            />
          )}
        </Row>
        {editingName && usernameError && (
          <div style={{ padding: "0 16px 10px", fontSize: 12, color: "var(--danger)" }}>
            {usernameError}
          </div>
        )}
      </Group>

      <Group title={t("settings.budget")}>
        <Row label={t("settings.monthlyLimit")}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <input
              type="number"
              inputMode="decimal"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              aria-label={t("settings.monthlyLimit")}
              className="num"
              style={{
                border: "1px solid var(--line)",
                borderRadius: 8,
                padding: "6px 10px",
                fontSize: 16,
                outline: "none",
                width: 120,
                textAlign: "right",
                fontWeight: 500,
              }}
            />
            <span className="num" style={{ color: "var(--muted)" }}>
              L
            </span>
          </div>
        </Row>
        <Row label={t("settings.currency")}>
          <span style={{ fontSize: 14, color: "var(--ink-2)" }}>L ALL</span>
        </Row>
      </Group>

      <Group title={t("settings.categoriesTitle")}>
        {categories.length === 0 ? (
          <div style={{ padding: "14px 16px", fontSize: 13, color: "var(--muted)" }}>
            {t("settings.noCategories")}
          </div>
        ) : (
          categories.map((c, i) => {
            const isEditing = editingCatId === c.id;
            const isOther = c.id === OTHER_CATEGORY_ID;
            const displayLabel = c.label;
            return (
              <div
                key={c.id}
                style={{
                  padding: "10px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  borderTop: i === 0 ? "none" : "1px solid var(--line-2)",
                }}
              >
                {isEditing ? (
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <EmojiButton
                        value={catEmojiDraft}
                        open={pickerOpen === "edit"}
                        onToggle={() => togglePicker("edit")}
                      />
                      <input
                        value={catLabelDraft}
                        onChange={(e) => setCatLabelDraft(e.target.value)}
                        maxLength={CATEGORY_LABEL_MAX}
                        aria-label={t("settings.categoryLabel")}
                        autoFocus
                        style={{
                          flex: 1,
                          border: "1px solid var(--line)",
                          borderRadius: 8,
                          padding: "6px 10px",
                          fontSize: 16,
                          outline: "none",
                          background: "var(--bg)",
                        }}
                      />
                      <button
                        onClick={saveEditCategory}
                        style={{
                          padding: "6px 10px",
                          borderRadius: 8,
                          background: "var(--ink)",
                          color: "#fff",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {t("common.save")}
                      </button>
                      <button
                        onClick={cancelEditCategory}
                        style={{
                          padding: "6px 10px",
                          borderRadius: 8,
                          background: "var(--surface)",
                          border: "1px solid var(--line)",
                          fontSize: 12,
                          color: "var(--muted)",
                        }}
                      >
                        {t("common.cancel")}
                      </button>
                    </div>
                    {pickerOpen === "edit" && (
                      <EmojiGrid
                        selected={catEmojiDraft}
                        onPick={(e) => {
                          setCatEmojiDraft(e);
                          setPickerOpen(null);
                        }}
                      />
                    )}
                  </div>
                ) : (
                  <>
                    <span
                      aria-hidden="true"
                      style={{
                        width: 28,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                        lineHeight: 1,
                      }}
                    >
                      {c.emoji}
                    </span>
                    <button
                      onClick={() => beginEditCategory(c)}
                      style={{
                        flex: 1,
                        textAlign: "left",
                        fontSize: 14,
                        color: "var(--ink)",
                      }}
                    >
                      {displayLabel}
                      {isOther && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            color: "var(--muted)",
                            fontWeight: 500,
                          }}
                        >
                          {t("settings.fallback")}
                        </span>
                      )}
                    </button>
                    {!isOther && (
                      <button
                        onClick={() => removeCategory(c.id)}
                        aria-label={t("settings.removeLabel", { label: displayLabel })}
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: 999,
                          background: "var(--surface)",
                          border: "1px solid var(--line)",
                          fontSize: 14,
                          color: "var(--muted)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        ×
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })
        )}
        <div
          style={{
            padding: "12px 16px",
            borderTop: "1px solid var(--line-2)",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <EmojiButton
              value={newCatEmoji}
              open={pickerOpen === "new"}
              onToggle={() => togglePicker("new")}
            />
            <input
              value={newCatLabel}
              onChange={(e) => setNewCatLabel(e.target.value)}
              placeholder={t("settings.newCategoryPlaceholder")}
              aria-label={t("settings.newCategoryAria")}
              maxLength={CATEGORY_LABEL_MAX}
              style={{
                flex: 1,
                border: "1px solid var(--line)",
                borderRadius: 8,
                padding: "8px 10px",
                fontSize: 16,
                outline: "none",
                background: "var(--bg)",
              }}
            />
            <button
              onClick={addCategory}
              disabled={!newCatValid}
              style={{
                padding: "8px 14px",
                borderRadius: 10,
                background: newCatValid ? "var(--ink)" : "var(--line)",
                color: newCatValid ? "#fff" : "var(--muted)",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {categories.length >= CATEGORIES_MAX ? t("common.max") : t("common.add")}
            </button>
          </div>
          {pickerOpen === "new" && (
            <EmojiGrid
              selected={newCatEmoji}
              onPick={(e) => {
                setNewCatEmoji(e);
                setPickerOpen(null);
              }}
            />
          )}
        </div>
      </Group>

      <Group title={t("settings.quickAdds")}>
        {quickAdds.length === 0 ? (
          <div
            style={{
              padding: "14px 16px",
              fontSize: 13,
              color: "var(--muted)",
            }}
          >
            {t("settings.noQuickAdds")}
          </div>
        ) : (
          quickAdds.map((qa, i) => (
            <div
              key={i}
              style={{
                padding: "10px 16px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                borderTop: "1px solid var(--line-2)",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 22,
                  height: 22,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  lineHeight: 1,
                }}
              >
                {categories.find((c) => c.id === qa.cat)?.emoji ?? "•"}
              </span>
              <span style={{ flex: 1, fontSize: 14, color: "var(--ink)" }}>{qa.label}</span>
              <span className="num" style={{ fontSize: 14, color: "var(--ink-2)" }}>
                {fmt(qa.amount)}
              </span>
              <button
                onClick={() => removeQuickAdd(i)}
                aria-label={t("settings.removeLabel", { label: qa.label })}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 999,
                  background: "var(--surface)",
                  border: "1px solid var(--line)",
                  fontSize: 14,
                  color: "var(--muted)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>
          ))
        )}
        <div
          style={{
            padding: "12px 16px",
            borderTop: "1px solid var(--line-2)",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={qaLabel}
              onChange={(e) => setQaLabel(e.target.value)}
              placeholder={t("settings.labelPlaceholder")}
              aria-label={t("settings.quickAddLabel")}
              maxLength={30}
              style={{
                flex: 1,
                border: "1px solid var(--line)",
                borderRadius: 8,
                padding: "8px 10px",
                fontSize: 16,
                outline: "none",
                background: "var(--bg)",
              }}
            />
            <input
              type="number"
              inputMode="decimal"
              value={qaAmount}
              onChange={(e) => setQaAmount(e.target.value)}
              placeholder="L"
              aria-label={t("settings.quickAddAmount")}
              className="num"
              style={{
                width: 80,
                border: "1px solid var(--line)",
                borderRadius: 8,
                padding: "8px 10px",
                fontSize: 16,
                outline: "none",
                background: "var(--bg)",
                textAlign: "right",
              }}
            />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(40px, 1fr))",
              gap: 4,
            }}
          >
            {categories.map((c) => {
              const active = qaCat === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setQaCat(c.id)}
                  aria-label={c.label}
                  aria-pressed={active}
                  style={{
                    padding: "8px 0",
                    borderRadius: 8,
                    background: active ? "var(--ink)" : "var(--bg)",
                    border: "1px solid " + (active ? "var(--ink)" : "var(--line)"),
                    color: active ? "#fff" : "var(--ink-2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    lineHeight: 1,
                  }}
                >
                  <span aria-hidden="true">{c.emoji}</span>
                </button>
              );
            })}
          </div>
          <button
            onClick={addQuickAdd}
            disabled={!qaValid}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              background: qaValid ? "var(--ink)" : "var(--line)",
              color: qaValid ? "#fff" : "var(--muted)",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {quickAdds.length >= QUICK_ADDS_MAX
              ? t("common.limitReached")
              : t("common.add")}
          </button>
        </div>
      </Group>

      <Group title={t("settings.preferences")}>
        <Row label={t("settings.language")}>
          <LanguageToggle current={currentLang} onChange={setLanguage} />
        </Row>
        <Row label={t("settings.autoOpen")}>
          <PrefToggle
            value={user.preferences.autoOpenAdd}
            onChange={(v) => onSetPreference("autoOpenAdd", v)}
          />
        </Row>
        <Row label={t("settings.showDaily")}>
          <PrefToggle
            value={user.preferences.showDailyAllowance}
            onChange={(v) => onSetPreference("showDailyAllowance", v)}
          />
        </Row>
      </Group>

      {error && (
        <div style={{ marginBottom: 16, color: "var(--danger)", fontSize: 13 }}>{error}</div>
      )}

      <Group title={t("settings.about")}>
        <LinkRow label={t("settings.privacyPolicy")} onClick={onOpenPrivacy} />
        <LinkRow label={t("settings.termsOfService")} onClick={onOpenTerms} />
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 16px",
            borderTop: "1px solid var(--line-2)",
            textDecoration: "none",
            color: "var(--ink)",
            fontSize: 14,
          }}
        >
          <span>{t("settings.contactSupport")}</span>
          <span style={{ color: "var(--muted)", fontSize: 18, lineHeight: 1 }}>›</span>
        </a>
      </Group>

      <Group title={t("settings.session")}>
        <button
          onClick={() => onLogout()}
          style={{
            width: "100%",
            padding: "14px 16px",
            textAlign: "left",
            color: "var(--ink)",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {t("settings.logout")}
        </button>
      </Group>

      <Group title={t("settings.dangerZone")}>
        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            style={{
              width: "100%",
              padding: "14px 16px",
              textAlign: "left",
              color: "var(--danger)",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {t("settings.deleteAccount")}
          </button>
        ) : (
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 12 }}>
              {t("settings.deleteConfirm")}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => setConfirmDelete(false)}
                style={{
                  flex: 1,
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "1px solid var(--line)",
                  background: "var(--surface)",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={() => onDeleteAccount()}
                style={{
                  flex: 1,
                  padding: "10px 14px",
                  borderRadius: 10,
                  background: "var(--danger)",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {t("settings.deleteAccount")}
              </button>
            </div>
          </div>
        )}
      </Group>
    </div>
  );
}

function LanguageToggle({
  current,
  onChange,
}: {
  current: Lang;
  onChange: (lang: Lang) => void;
}) {
  const options: { value: Lang; label: string }[] = [
    { value: "al", label: "AL" },
    { value: "en", label: "EN" },
  ];
  return (
    <div
      role="group"
      style={{
        display: "inline-flex",
        background: "var(--surface-2)",
        border: "1px solid var(--line)",
        borderRadius: 999,
        padding: 2,
      }}
    >
      {options.map((opt) => {
        const active = opt.value === current;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            style={{
              padding: "6px 14px",
              borderRadius: 999,
              background: active ? "var(--ink)" : "transparent",
              color: active ? "#fff" : "var(--ink-2)",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: ".06em",
              border: "none",
              cursor: "pointer",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function LinkRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: "14px 16px",
        background: "transparent",
        border: "none",
        borderTop: "1px solid var(--line-2)",
        color: "var(--ink)",
        fontSize: 14,
        textAlign: "left",
        cursor: "pointer",
      }}
    >
      <span>{label}</span>
      <span style={{ color: "var(--muted)", fontSize: 18, lineHeight: 1 }}>›</span>
    </button>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: "var(--muted)",
          letterSpacing: ".1em",
          textTransform: "uppercase",
          padding: "0 4px 8px",
        }}
      >
        {title}
      </div>
      <div
        style={{
          background: "var(--surface)",
          borderRadius: 14,
          border: "1px solid var(--line)",
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: "12px 16px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderTop: "1px solid var(--line-2)",
      }}
    >
      <span style={{ fontSize: 14, color: "var(--ink)" }}>{label}</span>
      {children}
    </div>
  );
}

function PrefToggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (next: boolean) => void | Promise<void>;
}) {
  return (
    <button
      onClick={() => void onChange(!value)}
      role="switch"
      aria-checked={value}
      style={{
        width: 44,
        height: 26,
        borderRadius: 999,
        background: value ? "var(--accent)" : "var(--line)",
        position: "relative",
        transition: "background .2s",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 2,
          left: value ? 20 : 2,
          width: 22,
          height: 22,
          borderRadius: 999,
          background: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
          transition: "left .2s",
        }}
      />
    </button>
  );
}

function EmojiButton({
  value,
  open,
  onToggle,
}: {
  value: string;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={t("settings.chooseEmoji")}
      aria-expanded={open}
      style={{
        width: 40,
        height: 36,
        borderRadius: 8,
        border: "1px solid " + (open ? "var(--ink)" : "var(--line)"),
        background: "var(--bg)",
        fontSize: 18,
        lineHeight: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: value ? "var(--ink)" : "var(--muted)",
      }}
    >
      <span aria-hidden="true">{value || "🙂"}</span>
    </button>
  );
}

function EmojiGrid({
  selected,
  onPick,
}: {
  selected: string;
  onPick: (emoji: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="listbox"
      aria-label={t("settings.emojiPicker")}
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(34px, 1fr))",
        gap: 4,
        padding: 8,
        borderRadius: 10,
        border: "1px solid var(--line)",
        background: "var(--surface)",
        maxHeight: 180,
        overflowY: "auto",
      }}
    >
      {EMOJI_PALETTE.map((e) => {
        const active = e === selected;
        return (
          <button
            key={e}
            type="button"
            onClick={() => onPick(e)}
            aria-label={t("settings.pickEmoji", { emoji: e })}
            aria-selected={active}
            style={{
              width: "100%",
              height: 32,
              borderRadius: 6,
              border: active ? "1px solid var(--ink)" : "1px solid transparent",
              background: active ? "var(--surface-2)" : "transparent",
              fontSize: 18,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span aria-hidden="true">{e}</span>
          </button>
        );
      })}
    </div>
  );
}
